import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import clsx from 'clsx';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import PropTypes from 'prop-types';

import { ProjectBackgroundTypes } from '../../constants/Enums';
import Paths from '../../constants/Paths';
import ProjectAddPopup from '../ProjectAddPopup';
import { Button, ButtonVariant, Icon, IconType, IconSize } from '../Utils';

import * as bs from '../../backgrounds.module.scss';
import * as gs from '../../global.module.scss';
import * as s from './Projects.module.scss';

const Projects = React.memo(({ projects, filteredProjects, isFiltered, canAdd, defaultData, isSubmitting, onCreate }) => {
  const [t] = useTranslation();

  const getProjectsText = () => {
    if (!isFiltered) {
      return `${t('common.showing')} ${t('common.projects', { count: projects.length, context: 'title' })}`;
    }
    return `${t('common.showing')} ${t('common.ofProjects', { filteredCount: filteredProjects.length, count: projects.length, context: 'title' })}`;
  };

  const getBoardsText = () => {
    const boardsCount = filteredProjects.reduce((sum, project) => sum + (project.boards ? project.boards.length : 0), 0);
    return `[${t('common.boards', { count: boardsCount, context: 'title' })}]`;
  };

  const getProjectInitial = (name) => {
    const trimmed = name?.trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
  };

  return (
    <div className={s.wrapper}>
      <div className={s.pageHeader}>
        <div className={s.pageHeaderMain}>
          <h1 className={s.pageTitle}>{t('common.projects', { count: projects.length, context: 'title' })}</h1>
          <div className={s.pageMeta}>
            <span>{getProjectsText()}</span>
            <span className={s.pageMetaChip}>{getBoardsText()}</span>
          </div>
        </div>
        <div className={s.headerButtonGroup}>
          {canAdd && (
            <ProjectAddPopup defaultData={defaultData} isSubmitting={isSubmitting} onCreate={onCreate} offset={16} position="bottom">
              <Button variant={ButtonVariant.Primary} title={t('common.addProject')} className={s.addButton}>
                <Icon type={IconType.Plus} size={IconSize.Size16} className={s.addButtonIcon} />
                {t('common.addProject')}
              </Button>
            </ProjectAddPopup>
          )}
        </div>
      </div>
      <div className={clsx(s.projectsWrapper, gs.scrollableY, projects.length === 0 && s.projectsWrapperEmpty)}>
        {filteredProjects.map((item) => {
          const hasCustomBackground = item.background && (item.background.type === ProjectBackgroundTypes.GRADIENT || item.background.type === ProjectBackgroundTypes.IMAGE);

          return (
            <div
              key={item.id}
              className={clsx(
                s.projectWrapper,
                hasCustomBackground && s.projectWrapperHasBackground,
                item.background && item.background.type === ProjectBackgroundTypes.GRADIENT && bs[`background${upperFirst(camelCase(item.background.name))}`],
              )}
              style={{
                background: item.background && item.background.type === ProjectBackgroundTypes.IMAGE && `url("${item.backgroundImage.coverUrl}") center / cover`,
              }}
            >
              {item.notificationsTotal > 0 && <span className={s.notification}>{item.notificationsTotal}</span>}
              <Link to={Paths.PROJECTS.replace(':id', item.id)} className={s.projectLink}>
                <div className={s.project}>
                  <div className={s.projectDecor} aria-hidden="true">
                    <span className={s.projectDecorOrb} />
                    <span className={s.projectDecorGrid} />
                  </div>
                  <div className={s.projectHeader}>
                    <div className={s.projectAvatar}>{getProjectInitial(item.name)}</div>
                    <div className={s.projectMeta}>
                      <span className={s.projectMetaChip}>
                        <Icon type={IconType.Board} size={IconSize.Size10} className={s.projectMetaIcon} />
                        {t('common.boards', { count: item.boards?.length || 0 })}
                      </span>
                      {(item.memberships?.length || 0) > 0 && (
                        <span className={s.projectMetaChip}>
                          <Icon type={IconType.Users} size={IconSize.Size10} className={s.projectMetaIcon} />
                          {item.memberships.length}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={s.projectBody}>
                    <div title={item.name} className={s.projectTitle}>
                      {item.name}
                    </div>
                    <span className={s.projectOpenHint} aria-hidden="true">
                      <Icon type={IconType.ArrowLeftBig} size={IconSize.Size14} className={s.projectOpenIcon} />
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          );
        })}
        {projects.length === 0 && !canAdd && (
          <div className={clsx(s.info, gs.emptyState)} role="status" aria-live="polite">
            <p>{t('common.needInvite')}</p>
          </div>
        )}
        {projects.length === 0 && canAdd && (
          <div className={clsx(s.info, gs.emptyState)} role="status" aria-live="polite">
            <p>{t('common.addProject')}</p>
            <ProjectAddPopup defaultData={defaultData} isSubmitting={isSubmitting} onCreate={onCreate} offset={16} position="bottom">
              <Button variant={ButtonVariant.Primary} title={t('common.addProject')} className={s.addButton}>
                <Icon type={IconType.Plus} size={IconSize.Size16} className={s.addButtonIcon} />
                {t('common.addProject')}
              </Button>
            </ProjectAddPopup>
          </div>
        )}
      </div>
    </div>
  );
});

Projects.propTypes = {
  projects: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  filteredProjects: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  isFiltered: PropTypes.bool.isRequired,
  canAdd: PropTypes.bool.isRequired,
  defaultData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  isSubmitting: PropTypes.bool.isRequired,
  onCreate: PropTypes.func.isRequired,
};

export default Projects;
